import type { Constructor, Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import type { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { DurchfuehrungsTag } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import { ProductLKK } from '@chancenwerk/shared/_cms/products/entities/ProductLKK.js';
import type { ProductLUS } from '@chancenwerk/shared/_cms/products/entities/ProductLUS.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { CourseAbschluss, CourseAbschlussType } from '@chancenwerk/shared/courses/CourseAbschluss.js';
import { CourseDigital } from '@chancenwerk/shared/courses/CourseDigital.js';
import { CourseIntensive } from '@chancenwerk/shared/courses/CourseIntensive.js';
import {
  CourseLernfoerderung,
  CourseLernfoerderungVersion,
  courseMatchesAnyDurchfuehrungsTag,
} from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { CourseLUS } from '@chancenwerk/shared/courses/CourseLUS.js';
import { Subject, Weekday } from '@chancenwerk/enums/enums.js';
import '@chancenwerk/client/design/components/cr-date-picker-dropdown.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/design/components/cr-nospace-toggle-content.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import type { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { takeUntil, tap } from 'rxjs';

export class CourseProxy extends AdornisEntity {
  public static override _class = 'CourseProxy';

  @Field(() => String, {
    default: v => v ?? '',
  })
  public location!: string;

  @Field(() => [Course], {
    default: v => v ?? [],
  })
  public courses!: Course[];
}

/**
 * @customElement board-dialog-edit-course
 */
@customElement('board-dialog-edit-course')
export class BoardDialogEditCourse extends ChromiumDialog<void> {
  static override get element_name() {
    return 'board-dialog-edit-course';
  }

  @property({ attribute: false }) public kpf = new RXController<undefined | KPF>(this, undefined);
  @property({ attribute: false }) public courseType = new RXController(this, CourseLernfoerderung._class);
  @property({ attribute: false }) public courseProxy = new RXController<CourseProxy>(
    this,
    new CourseProxy({
      courses: [new CourseLernfoerderung({})],
    }),
  );
  @property({ attribute: false }) public new = true;

  @state() private _errorMsg = '';
  @state() private _loading = false;

  override heading = 'Kurs editieren';

  @state() _form = new FormController(this, this.courseProxy.observable, {
    success: async () => {
      if (!this.kpf.value && this.courseType.value !== CourseDigital._class) {
        this._errorMsg = 'Das KPF für diesen Kurs wurde nicht gesetzt! Sollte nicht passieren!';
        this._loading = false;
        return;
      }

      // * Check wether the course is new or is an already existing course
      if (this.new) {
        await this._createNew();
      } else {
        //* All other courses can normally be updated
        for (const course of this.courseProxy.value.courses) {
          if (this.kpf.value) {
            course.kpfID = this.kpf.value._id;
          }
          await course.save();
        }
      }

      this._loading = false;
      await this.close();
    },
    error: (e: ValidationError<any>) => {
      console.error(e);
      ChromiumSnackbar.show(`Fehler beim Speichern: ${e.message}`).catch(console.error);
      this._loading = false;
    },
  });

  override connectedCallback(): void {
    super.connectedCallback();
    this.courseProxy.observable
      .pipe(
        takeUntil(this.disconnected),
        tap(c => {
          if (!this.new) {
            this.heading = 'Kurs editieren';
          } else {
            this.heading = 'Kurs erstellen';
          }
        }),
      )
      .subscribe();
  }

  private async _createNew() {
    for (const course of this.courseProxy.value.courses) {
      if (this.kpf.value) {
        course.kpfID = this.kpf.value._id;
      }

      if (course._id) {
        await course.save();
      } else {
        try {
          // * Ensure that we create the correct version
          if (this._isV2LF() && course instanceof CourseLernfoerderung) {
            course.version = CourseLernfoerderungVersion.V2;
          }
          await course.create();
        } catch (e: any) {
          console.error(e);
        }
      }
    }
  }

  private _isLUS() {
    return this.courseType.value === CourseLUS._class;
  }

  private _isLF() {
    return this.courseType.value === CourseLernfoerderung._class;
  }

  private _isV2LF() {
    return (this.courseProxy.value.courses[0] instanceof CourseLernfoerderung &&
      this.courseProxy.value.courses[0].version === CourseLernfoerderungVersion.V2) ||
      this.kpf.value?.produkt instanceof ProductLKK
      ? (this.kpf.value?.produkt as ProductLKK).useV2Courses
      : false;
  }

  private _isAbschluss() {
    return this.courseType.value === CourseAbschluss._class;
  }

  private _firstCourse() {
    return this.courseProxy.value.courses[0];
  }

  /**
   * Calls fn for each course of type T
   * @param type Class of the course
   * @param fn Function to call for each course
   */
  private _setAllCourses<T extends Course>(type: Constructor<T>, fn: (val: T) => void) {
    if (this._firstCourse() instanceof type) {
      this.courseProxy.value.courses.forEach(c => {
        const course = c as T;
        fn(course);
      });
      this.requestUpdate();
    }
  }

  private _lkkAdvicedRooms() {
    return html`
      ${(this._isLF() && !this._isV2LF()) || this._isLUS()
        ? html` <cr-card ${css({ backgroundColor: this.colors.neutral.shade(50) })} space="md">
            <cr-text bold>Im CHANCENraum sind folgende Räume hinterlegt:</cr-text>
            <cr-text
              >${[
                ...new Set(
                  (this.kpf.value?.produkt as ProductLKK | ProductLUS).durchfuehrungsTage?.flatMap(t => t.locations),
                ),
              ].join(', ')}</cr-text
            >
          </cr-card>`
        : nothing}
    `;
  }

  private _roomInput() {
    if (this._isV2LF()) {
      return html`<cr-card ${css({ backgroundColor: this.colors.neutral.shade(50) })} space="md">
        <cr-text>
          Für dieses KPF ist die neue Zuteilung aktiv. Das bedeutet das keine Zuteilung mehr nach Räumen vorgenommen
          wird!
        </cr-text>
      </cr-card>`;
    }

    return html`
      <cr-input
        .value=${this.courseProxy.value.courses.at(0)?.location}
        placeholder=${[CourseDigital._class, CourseAbschluss._class].includes(this.courseType.value) ? 'Link' : 'Raum'}
        @value-picked=${(e: ValueEvent<Maybe<string>>) => {
          for (const course of this.courseProxy.value.courses) {
            course.location = e.detail.value ?? '';
          }
        }}
      >
      </cr-input>
    `;
  }

  override content() {
    return html`
      <cr-flex space="md">
        <cr-text ${css({ color: this.colors.tone.error })}> ${this._errorMsg} </cr-text>
        ${this._lkkAdvicedRooms()} ${this._roomInput()}
        ${this.courseProxy.value.courses.at(0) instanceof CourseAbschluss
          ? html` <cr-input
              .value=${(this.courseProxy.value.courses.at(0) as Maybe<CourseAbschluss>)?.diKuLink}
              placeholder=${'DiKu Link'}
              @value-picked=${(e: ValueEvent<Maybe<string>>) => {
                for (const course of this.courseProxy.value.courses) {
                  if (course instanceof CourseAbschluss) {
                    course.diKuLink = e.detail.value ?? '';
                  }
                }
              }}
            >
            </cr-input>`
          : nothing}
        <cr-flex space="sm" horizontal center crossaxis-center>
          ${this.courseProxy.value.courses.at(0) instanceof CourseAbschluss
            ? nothing
            : html`<cr-date-picker-dropdown
                  .end-year=${DateTime.now().plus({ years: 10 }).year}
                  placeholder="geplantes Startdatum"
                  click-to-save
                  flex
                  clearable
                  .value=${this.courseProxy.value.courses.at(0)?.plannedStartDate}
                  @value-picked=${(e: ValueEvent<DateTime>) => {
                    for (const course of this.courseProxy.value.courses) {
                      course.plannedStartDate = e.detail.value;
                    }
                    this.requestUpdate();
                  }}
                ></cr-date-picker-dropdown>
                <cr-h4>-</cr-h4>`}
          <cr-date-picker-dropdown
            .end-year=${DateTime.now().plus({ years: 10 }).year}
            placeholder=${this._isAbschluss() ? 'Startdatum' : '"tatsächliches Startdatum"'}
            click-to-save
            flex
            clearable
            .value=${this.courseProxy.value.courses.at(0)?.startDate}
            @value-picked=${(e: ValueEvent<DateTime>) => {
              for (const course of this.courseProxy.value.courses) {
                course.startDate = e.detail.value;
              }
              this.requestUpdate();
            }}
          ></cr-date-picker-dropdown>
          ${this.courseProxy.value.courses.at(0) instanceof CourseAbschluss
            ? html`<cr-date-picker-dropdown
                .end-year=${DateTime.now().plus({ years: 10 }).year}
                placeholder="Enddatum"
                click-to-save
                flex
                clearable
                .value=${(this.courseProxy.value.courses.at(0) as Maybe<CourseAbschluss>)?.endDate}
                @value-picked=${(e: ValueEvent<DateTime>) => {
                  for (const course of this.courseProxy.value.courses) {
                    if (course instanceof CourseAbschluss) {
                      course.endDate = e.detail.value;
                    }
                  }
                  this.requestUpdate();
                }}
              ></cr-date-picker-dropdown>`
            : nothing}
        </cr-flex>
        ${this._intensiveCourseFields()} ${this._digitalCourseFields()} ${this._abschlussCourseFields()}
        ${repeat(
          this.courseProxy.value.courses,
          c => c._id,
          (course, index) => {
            return this._courseFormFields(course, index);
          },
        )}
      </cr-flex>
    `;
  }

  private _intensiveCourseFields() {
    if (this.courseType.value !== CourseIntensive._class) {
      return nothing;
    }

    return html`<cr-dropdown-search-selection
        flex
        clearable
        placeholder="Fach 1"
        .selectable=${Object.values(Subject).filter(s => s !== Subject.NATURWISSENSCHAFTEN)}
        .search=${(search?: string) =>
          Object.values(Subject)
            .filter(s => s !== Subject.BIOLOGIE && s !== Subject.PHYSIK && s !== Subject.CHEMIE)
            .filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
        .value=${(this._firstCourse() as CourseIntensive).subjectOne}
        @value-changed=${(e: ValueEvent<string>) => {
          this._setAllCourses(CourseIntensive, c => {
            c.subjectOne = e.detail.value;
          });
        }}
      ></cr-dropdown-search-selection
      ><cr-dropdown-search-selection
        flex
        clearable
        placeholder="Fach 2"
        .selectable=${Object.values(Subject).filter(s => s !== Subject.NATURWISSENSCHAFTEN)}
        .search=${(search?: string) =>
          Object.values(Subject)
            .filter(s => s !== Subject.BIOLOGIE && s !== Subject.PHYSIK && s !== Subject.CHEMIE)
            .filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
        .value=${(this._firstCourse() as CourseIntensive).subjectTwo}
        @value-changed=${(e: ValueEvent<string>) => {
          this._setAllCourses(CourseIntensive, c => {
            c.subjectTwo = e.detail.value;
          });
        }}
      ></cr-dropdown-search-selection>`;
  }

  private _digitalCourseFields() {
    if (this.courseType.value !== CourseDigital._class) {
      return nothing;
    }

    return html` <cr-flex crossaxis-center horizontal space="sm" space-between>
      <cr-dropdown-search-selection
        flex
        clearable
        placeholder="Fach"
        .selectable=${Object.values(Subject).filter(s => s !== Subject.NATURWISSENSCHAFTEN)}
        .search=${(search?: string) =>
          Object.values(Subject)
            .filter(s => s !== Subject.BIOLOGIE && s !== Subject.PHYSIK && s !== Subject.CHEMIE)
            .filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
        .value=${(this._firstCourse() as CourseDigital).subject}
        @value-changed=${(e: ValueEvent<string>) => {
          this._setAllCourses(CourseDigital, c => {
            c.subject = e.detail.value as Subject;
          });
        }}
      ></cr-dropdown-search-selection>
      <cr-dropdown-selection
        ${css({ flex: '1', flexWrap: 'wrap' })}
        .value=${(this._firstCourse() as CourseDigital).jahrgangsstufe}
        placeholder="Jahrgangsstufe"
        .selectables=${[8, 9, 10, 11, 12, 13]}
        @value-picked=${(e: ValueEvent<number>) => {
          this._setAllCourses(CourseDigital, c => {
            c.jahrgangsstufe = e.detail.value;
          });
        }}
      ></cr-dropdown-selection>
    </cr-flex>`;
  }

  private _abschlussCourseFields() {
    if (this.courseType.value !== CourseAbschluss._class) {
      return nothing;
    }

    return html`<cr-dropdown-search-selection
        flex
        clearable
        placeholder="Fach"
        .selectable=${Object.values(Subject).filter(s => s !== Subject.NATURWISSENSCHAFTEN)}
        .search=${(search?: string) =>
          Object.values(Subject).filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
        .value=${(this._firstCourse() as CourseAbschluss).subject}
        @value-changed=${(e: ValueEvent<string>) => {
          this._setAllCourses(CourseAbschluss, c => {
            c.subject = e.detail.value as Subject;
          });
        }}
      ></cr-dropdown-search-selection>
      <cr-dropdown-search-selection
        flex
        clearable
        placeholder="Abschluss"
        .selectable=${Object.values(CourseAbschlussType)}
        .search=${(search?: string) =>
          Object.values(CourseAbschlussType).filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
        .value=${(this._firstCourse() as CourseAbschluss).type}
        @value-changed=${(e: ValueEvent<string>) => {
          this._setAllCourses(CourseAbschluss, c => {
            c.type = e.detail.value as CourseAbschlussType;
          });
        }}
      ></cr-dropdown-search-selection>
      <cr-input
        type="number"
        placeholder="Jahrganstufe"
        .value=${(this._firstCourse() as CourseAbschluss).class}
        @value-picked=${(e: ValueEvent<number>) => {
          this._setAllCourses(CourseAbschluss, c => {
            c.class = e.detail.value;
          });
        }}
      ></cr-input>
      <cr-input
        type="number"
        placeholder="Maximale Teilnehmerzahl"
        .value=${(this._firstCourse() as CourseAbschluss).maxAttendanceCount}
        @value-picked=${(e: ValueEvent<number>) => {
          this._setAllCourses(CourseAbschluss, c => {
            c.maxAttendanceCount = e.detail.value;
          });
        }}
      ></cr-input>
      <cr-checkbox
        label="Offen für alle Schulen"
        .value=${(this._firstCourse() as CourseAbschluss).openToAll}
        @value-picked=${(e: ValueEvent<boolean>) => {
          this._setAllCourses(CourseAbschluss, c => {
            c.openToAll = e.detail.value;
          });
        }}
      ></cr-checkbox> `;
  }

  private _lkkDays(course: Course) {
    return html`<cr-grid columns="150px 50px 50px 50px">
      <cr-text bold> ${course.weekday}</cr-text>
      <cr-text> ${course.startTime?.toFormat('HH:mm')} </cr-text>
      <cr-text ${css({ textAlign: 'center' })}> - </cr-text>
      <cr-text> ${course.endTime?.toFormat('HH:mm')} </cr-text>
    </cr-grid>`;
  }

  private _lkkFormFields(course: Course, index: number) {
    // * Check if there is wrong data because of old data
    const days = (this.kpf.value?.produkt as ProductLKK | ProductLUS).durchfuehrungsTage ?? [];
    if (!courseMatchesAnyDurchfuehrungsTag(course, days)) {
      const choices = days.filter(d =>
        days.find(o => o.wochentag === d.wochentag && !o.sameTime(course.startTime, course.endTime)),
      );

      return html`
        <cr-nospace-toggle-content
          label="Falscher Tag / Uhrzeit: ${course.weekday ?? 'Wochentag'} ${course.startTime?.toFormat(
            'HH:mm',
          )} - ${course.endTime?.toFormat('HH:mm')}"
        >
          <cr-flex space="md">
            <cr-dropdown-selection
              placeholder="Richtigen Wählen"
              .selectables=${choices}
              .renderString=${(day: Maybe<DurchfuehrungsTag>) => {
                if (!day) {
                  return '';
                }
                return `${day.wochentag} ${day.startZeit?.toFormat('HH:mm')} - ${day.endZeit?.toFormat('HH:mm')}`;
              }}
              @value-picked=${(e: ValueEvent<Maybe<DurchfuehrungsTag>>) => {
                if (!e.detail.value) {
                  return;
                }

                course.startTime = e.detail.value.startZeit;
                course.endTime = e.detail.value.endZeit;
                course.weekday = e.detail.value.wochentag;
              }}
            ></cr-dropdown-selection>
          </cr-flex>
        </cr-nospace-toggle-content>
      `;
    }

    if (!this.new) {
      return nothing;
    }

    return this._lkkDays(course);
  }

  private _courseFormFields(course: Course, index: number) {
    // * If it is a Lernförderung we won't allow changing the times and weekday!
    if (this._isLF() || this._isLUS()) {
      return this._lkkFormFields(course, index);
    }

    return html`
      <cr-nospace-toggle-content label="${course.weekday ?? 'Wochentag'}">
        <cr-flex space="md">
          <cr-dropdown-selection
            clearable
            ${this._form.field('courses', index, 'weekday')}
            placeholder="Wochentag"
            .selectables=${Object.values(Weekday).filter(d => d !== Weekday.SATURDAY && d !== Weekday.SUNDAY)}
            @value-picked=${() => this.requestUpdate()}
          ></cr-dropdown-selection>
          <cr-flex crossaxis-center horizontal space="sm" space-between>
            <cr-input
              type="time"
              flex
              placeholder-mode="inline-floating"
              ${this._form.field('courses', index, 'startTime')}
              placeholder="Startzeit"
            ></cr-input>
            <cr-text> &dash; </cr-text>
            <cr-input
              type="time"
              flex
              placeholder-mode="inline-floating"
              ${this._form.field('courses', index, 'endTime')}
              placeholder="Endzeit"
            ></cr-input>
          </cr-flex>
        </cr-flex>
      </cr-nospace-toggle-content>
    `;
  }

  protected override footerContent() {
    return html`
      <cr-flex horizontal crossaxis-center end space="md">
        <cr-button
          .loading=${this._loading}
          icon="save"
          @click=${() => {
            this._loading = true;
            this._errorMsg = '';
            this._form
              .validate({
                recurse: false,
              })
              .catch(console.error);
          }}
        >
          ${this.new ? 'Erstellen' : 'Speichern'}
        </cr-button>
      </cr-flex>
    `;
  }

  public override getInnerWrapper(options: DialogOptions) {
    const wrapperElem = super.getInnerWrapper(options as Required<DialogOptions>);
    if (!options.draggable) {
      wrapperElem.style.setProperty('min-width', '35vw');
    }
    return wrapperElem;
  }
}
